import { Box, FormHelperText, IconButton, InputAdornment, Link, MenuItem, TextField, Typography } from "@mui/material";
import { AddCircleOutline, DeleteOutline } from "@mui/icons-material";
import React, { useState } from "react";
import { HankoHelpTooltip } from "../Tooltip/Tooltip";

interface Props {
  label: string
  helperText?: React.ReactNode
  value: Record<string, string>
  onChange: (next: Record<string, string>) => void
  valueLabel: string
  valueHelpText?: React.ReactNode
  // Required, not optional: Hanko hard-rejects (400) any mapping referencing a claim name that
  // isn't declared in the tenant's custom_claims.definitions, so the key field is a closed
  // picker, not free text - there's no meaningful way to use this component without a known set
  // to pick from.
  knownClaimNames: string[]
}

export const claimMappingEquals = (a: Record<string, string>, b: Record<string, string>): boolean => {
  const keysA = Object.keys(a)
  const keysB = Object.keys(b)
  return keysA.length === keysB.length && keysA.every((key) => a[key] === b[key])
}

// Controlled key/value mapping editor - no submit/API call of its own, the enclosing form owns
// that. Row identity is index-based (not keyed by the mapping key itself), since the key is
// exactly what's being edited and using it as the React key would remount the row - and drop
// focus - on every keystroke.
export const ClaimMappingEditor = (props: Props) => {
  const { label, helperText, value, onChange, valueLabel, valueHelpText, knownClaimNames } = props
  const entries = Object.entries(value)
  const [draftKey, setDraftKey] = useState("")
  const [draftValue, setDraftValue] = useState("")
  const availableToAdd = knownClaimNames.filter((name) => !(name in value))
  const nothingToAdd = availableToAdd.length === 0

  const updateRow = (index: number, key: string, mappedValue: string) => {
    const next: Record<string, string> = {}
    entries.forEach(([k, v], i) => {
      const [rowKey, rowValue] = i === index ? [key, mappedValue] : [k, v]
      if (rowKey.trim() !== "") {
        next[rowKey] = rowValue
      }
    })
    onChange(next)
  }

  const deleteRow = (index: number) => {
    const next: Record<string, string> = {}
    entries.forEach(([k, v], i) => {
      if (i !== index) {
        next[k] = v
      }
    })
    onChange(next)
  }

  const addDraftRow = () => {
    if (draftKey.trim() === "") {
      return
    }
    onChange({ ...value, [draftKey]: draftValue })
    setDraftKey("")
    setDraftValue("")
  }

  return <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
    <Typography variant="subtitle2">{label}</Typography>
    {helperText !== undefined ? <FormHelperText sx={{ mt: -1, mb: 1 }}>{helperText}</FormHelperText> : null}
    {entries.map(([key, mappedValue], index) => {
      // Excludes names already used by other rows, the same way availableToAdd does for the
      // draft row below - otherwise picking a name another row already has silently collides
      // both rows onto one key when the map is rebuilt, and one of them just disappears.
      const availableForRow = knownClaimNames.filter((name) => name === key || !(name in value))
      // A row's key can reference a claim that's since been removed from the tenant's declared
      // custom_claims (mapped, then the definition got deleted later) - keep it selectable
      // rather than have the picker silently drop an out-of-list value.
      const options = knownClaimNames.includes(key) ? availableForRow : [key, ...availableForRow]
      return (
        <Box key={index} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Claim name"
            value={key}
            onChange={(e) => updateRow(index, e.target.value, mappedValue)}
          >
            {options.map((name) => (
              <MenuItem key={name} value={name}>{name}</MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            size="small"
            label={valueLabel}
            value={mappedValue}
            onChange={(e) => updateRow(index, key, e.target.value)}
            InputProps={valueHelpText !== undefined ? {
              endAdornment: <InputAdornment position="end"><HankoHelpTooltip title={valueHelpText} /></InputAdornment>,
            } : undefined}
          />
          <IconButton onClick={() => deleteRow(index)} aria-label="Remove mapping" color="error">
            <DeleteOutline />
          </IconButton>
        </Box>
      )
    })}
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      <TextField
        select
        fullWidth
        size="small"
        label="Claim name"
        value={draftKey}
        disabled={nothingToAdd}
        onChange={(e) => setDraftKey(e.target.value)}
      >
        {availableToAdd.map((name) => (
          <MenuItem key={name} value={name}>{name}</MenuItem>
        ))}
      </TextField>
      <TextField
        fullWidth
        size="small"
        label={valueLabel}
        value={draftValue}
        disabled={nothingToAdd}
        onChange={(e) => setDraftValue(e.target.value)}
        InputProps={valueHelpText !== undefined ? {
          endAdornment: <InputAdornment position="end"><HankoHelpTooltip title={valueHelpText} /></InputAdornment>,
        } : undefined}
      />
      <IconButton onClick={addDraftRow} aria-label="Add mapping" color="primary" disabled={nothingToAdd || draftKey.trim() === ""}>
        <AddCircleOutline />
      </IconButton>
    </Box>
    {nothingToAdd ? (
      <FormHelperText>
        {knownClaimNames.length === 0
          ? <>This project has no custom claims declared yet - <Link href="custom_claims" target="_blank" rel="noopener noreferrer">declare one in Custom Claims settings</Link>, then edit this connection again to map it.</>
          : "All of this project's declared custom claims are already mapped here."}
      </FormHelperText>
    ) : null}
  </Box>
}
